import { z } from 'zod';

import { UuidSchema } from '../common/id/id';

import { LegalDocTypeSchema } from './doc-type/doc-type';

/**
 * One document a member still owes acceptance on.
 *
 * It carries the document's identity **and** the exact version, because those
 * answer two different questions the client has at once: which screen to render
 * (`docType`/`key`), and what to send back (`versionId`). The body is
 * deliberately absent — the consent screen fetches it per document, so a boot
 * check that lists five outstanding documents does not download five policies.
 */
export const RequiredConsentSchema = z
  .object({
    documentId: UuidSchema,
    key: z.string(),
    docType: LegalDocTypeSchema,
    versionId: UuidSchema,
    version: z.number().int().min(1),
    /** The active locale's title. */
    title: z.string(),
    effectiveAt: z.iso.datetime(),
    /**
     * `true` when the member has consented to an **earlier** version of this
     * document — i.e. this is a re-consent after a material change rather than
     * a first acceptance. It is what the grace window keys off, and what lets
     * the client say "our terms have changed" instead of "please accept our
     * terms".
     */
    isReconsent: z.boolean(),
  })
  .strict();

export type RequiredConsent = z.infer<typeof RequiredConsentSchema>;

/**
 * Everything outstanding for one member, split by whether it blocks **now**.
 *
 * The split is the `legal.reconsentGraceHours` setting made visible instead of
 * implicit: `required` is what the consent gate refuses on, `pending` is what
 * it will refuse on once the grace window closes. A client that only had one
 * list would either nag about something that is not yet enforced or discover
 * the block at the moment it lands, with no chance to warn first.
 *
 * `pending` is only ever a **re-consent** (see `RequiredConsent.isReconsent`):
 * a member who never accepted a document is blocked immediately, or the grace
 * setting would become a way to use the platform having agreed to nothing.
 */
export const RequiredConsentsSchema = z
  .object({
    required: z.array(RequiredConsentSchema),
    pending: z.array(RequiredConsentSchema),
  })
  .strict();

export type RequiredConsents = z.infer<typeof RequiredConsentsSchema>;
